import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import axios from "axios";
import SchoolIcon from "@mui/icons-material/School";
import PeopleIcon from "@mui/icons-material/People";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { STATS_API } from "../../constant";
// Icons + labels config — values come from the API
const STAT_CONFIG = [
  {
    key: "totalTests",
    icon: <SchoolIcon sx={{ fontSize: 22 }} />,
    labelKey: "statTests",
  },
  {
    key: "totalUsers",
    icon: <PeopleIcon sx={{ fontSize: 22 }} />,
    labelKey: "statUsers",
  },
  {
    key: "totalAttempts",
    icon: <AssignmentTurnedInIcon sx={{ fontSize: 22 }} />,
    labelKey: "statAttempts",
  },
  {
    key: "successRate",
    icon: <EmojiEventsIcon sx={{ fontSize: 22 }} />,
    labelKey: "statSuccess",
  },
];

const Hero = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.user);
  const canvasRef = useRef(null);
  const [apiStats, setApiStats] = useState(null);

  // Fetch live stats from server
  useEffect(() => {
    axios
      .get(STATS_API)
      .then(({ data }) => setApiStats(data))
      .catch((err) => console.error("Stats fetch error:", err));
  }, []);

  // Merge config with API values
  const statsDisplay = STAT_CONFIG.map((s) => ({
    ...s,
    value: apiStats
      ? s.key === "successRate"
        ? `${apiStats[s.key]}%`
        : apiStats[s.key].toLocaleString()
      : "—",
  }));

  // Animated particle canvas — canvas must stay in CSS (position/size can't be Tailwind alone)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const isDark = () => document.documentElement.classList.contains("dark");

    const particles = Array.from({ length: 55 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      r: Math.random() * 2.5 + 0.5,
      dx: (Math.random() - 0.5) * 0.4,
      dy: (Math.random() - 0.5) * 0.4,
      opacity: Math.random() * 0.5 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const dark = isDark();
      particles.forEach((p) => {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = dark
          ? `rgba(34,197,94,${p.opacity})`
          : `rgba(22,163,74,${p.opacity * 0.6})`;
        ctx.fill();
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dist = Math.hypot(
            particles[i].x - particles[j].x,
            particles[i].y - particles[j].y,
          );
          if (dist < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = dark
              ? `rgba(34,197,94,${0.08 * (1 - dist / 100)})`
              : `rgba(22,163,74,${0.05 * (1 - dist / 100)})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <section className="hero-section">
      {/* Particle canvas — z-[2], absolute inset (defined in index.css) */}
      <canvas ref={canvasRef} className="hero-canvas" />

      {/* Content — z-[3] above canvas and overlay */}
      <div className="relative z-[3] text-center px-5 max-w-[800px] mx-auto">
        {/* Badge */}
        <div className="animate-fade-up inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/25 text-[13px] font-semibold text-green-700 dark:text-green-400 mb-6 tracking-[0.3px]">
          <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse flex-shrink-0" />
          {t("hero.badge")}
        </div>

        {/* Title */}
        <h1 className="animate-fade-up-1 text-[clamp(2.2rem,5vw,4rem)] font-black leading-[1.1] tracking-[-1.5px] text-[#0f172a] dark:text-[#f1f5f9] mb-2">
          {t("hero.title1")}{" "}
          <span className="bg-gradient-to-br from-green-400 to-green-700 bg-clip-text text-transparent">
            {t("hero.adminPanel")}
          </span>
        </h1>

        {/* Subtitle */}
        <p className="animate-fade-up-2 text-[clamp(1rem,2vw,1.2rem)] font-medium text-slate-500 dark:text-slate-400 mt-4 mb-9 leading-relaxed max-w-[560px] mx-auto">
          {t("hero.subtitle")}
        </p>

        {/* CTA buttons */}
        <div className="animate-fade-up-3 flex items-center justify-center gap-3.5 flex-wrap mb-14">
          {user?.status === "Active" ? (
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-br from-green-400 to-green-600 text-white font-bold text-[15px] no-underline shadow-[0_6px_20px_rgba(34,197,94,0.35)] hover:shadow-[0_10px_28px_rgba(34,197,94,0.45)] hover:-translate-y-0.5 transition-all"
            >
              {t("hero.dashboardBtn")}{" "}
              <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Link>
          ) : (
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-br from-green-400 to-green-600 text-white font-bold text-[15px] no-underline shadow-[0_6px_20px_rgba(34,197,94,0.35)] hover:shadow-[0_10px_28px_rgba(34,197,94,0.45)] hover:-translate-y-0.5 transition-all"
            >
              {t("hero.loginBtn")} <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Link>
          )}
        </div>

        {/* Stat cards */}
        <div className="animate-fade-up-4 flex items-center justify-center gap-4 flex-wrap">
          {statsDisplay.map((stat, i) => (
            <div
              key={i}
              className="flex items-center gap-2.5 px-5 py-3.5 rounded-2xl bg-white/70 dark:bg-[#0f172a]/70 border border-green-500/15 backdrop-blur-sm shadow-[0_2px_12px_rgba(0,0,0,0.05)] dark:shadow-[0_2px_12px_rgba(0,0,0,0.3)] hover:-translate-y-[3px] hover:shadow-[0_8px_24px_rgba(34,197,94,0.15)] hover:border-green-500/35 transition-all cursor-default"
            >
              <div className="w-10 h-10 rounded-[10px] bg-gradient-to-br from-green-500/15 to-green-600/10 flex items-center justify-center text-green-700 dark:text-green-400 flex-shrink-0">
                {stat.icon}
              </div>
              <div>
                <div className="text-[18px] font-black text-[#0f172a] dark:text-[#f1f5f9] leading-none">
                  {stat.value}
                </div>
                <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-[0.5px] mt-0.5">
                  {t(`hero.${stat.labelKey}`)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll hint */}
      <div className="animate-fade-up-7 absolute bottom-7 left-1/2 -translate-x-1/2 z-[3] flex flex-col items-center gap-1.5">
        <div className="w-6 h-10 rounded-xl border-2 border-green-500/40 flex items-start justify-center pt-[5px]">
          <div className="w-1 h-2 rounded-sm bg-green-500 scroll-dot-inner" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
